<template>
  <div class="content">
    <div class="title-text">
      <p class="title">Agency Members</p>
      <p class="subtitle">
        Manage team members with access to all artists in your agency account.
      </p>
    </div>
    <div class="content-body">
      <div class="artist-team-card">
        <div class="card-content">

          <!-- Current agency members -->
          <div class="table-container full-width" v-if="agency_members.length">
            <table class="users-table">
              <thead>
                <tr class="head">
                  <th>
                    <div class="th-content"><span>Name</span></div>
                  </th>
                  <th>
                    <div class="th-content"><span>Email</span></div>
                  </th>
                  <th>
                    <div class="th-content actions"><span>Actions</span></div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="member in agency_members" :key="member.id" class="row">
                  <td class="email">
                    <span>{{ member.first_name }} {{ member.last_name }}</span>
                  </td>
                  <td class="email">
                    <span>{{ member.email }}</span>
                  </td>
                  <td class="actions">
                    <div class="actions-container">
                      <button @click="removeMember(member)">
                        <span>Remove</span>
                        <RemoveIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p v-else class="no-members-text">No agency members yet. Add team members from the list below.</p>

          <!-- Add agency members from eligible candidates -->
          <div class="invite-user-box" v-if="candidates.length">
            <div class="box-header">
              <UserPlusIcon />
              <span>Add Agency Member</span>
            </div>
            <div class="box-content">
              <span class="box-text">
                Select an existing artist team member to give them full access to all agency artists.
                Only team members without their own active subscription are shown.
              </span>
              <div class="table-container" style="margin-top: 18px; width: 100%;">
                <table class="users-table">
                  <thead>
                    <tr class="head">
                      <th><div class="th-content"><span>Name</span></div></th>
                      <th><div class="th-content"><span>Email</span></div></th>
                      <th><div class="th-content actions"><span>Actions</span></div></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="candidate in candidates" :key="candidate.user.id" class="row">
                      <td class="email">
                        <span>{{ candidate.user.first_name }} {{ candidate.user.last_name }}</span>
                      </td>
                      <td class="email">
                        <span>{{ candidate.user.email }}</span>
                      </td>
                      <td class="actions">
                        <div class="actions-container">
                          <button @click="addMember(candidate.user)" class="button-default">
                            <span>Add</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <p v-else-if="!agency_members.length" class="no-members-text">
            No eligible team members to add. Invite users to individual artists first.
          </p>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserPlusIcon from "../../../../public/img/icons/user-plus.svg";
import RemoveIcon from "../../../../public/img/icons/remove.svg";
import {
  Q_AGENCY_MEMBERS,
  Q_ELIGIBLE_AGENCY_CANDIDATES,
  M_ADD_AGENCY_MEMBER,
  M_REMOVE_AGENCY_MEMBER
} from "@/graphql";
import Swal from "sweetalert2";
import "@/assets/scss/custom/vendors-extensions/_sweetalert.scss";

export default {
  name: "AgencyMembers",
  components: {
    UserPlusIcon,
    RemoveIcon
  },
  data() {
    return {
      agency_members: [],
      candidates: []
    };
  },
  apollo: {
    agency_members: {
      query: Q_AGENCY_MEMBERS,
      variables() {
        return { userId: this.user.id };
      },
      update(data) {
        return data.users_by_pk?.agency_members || [];
      }
    },
    candidates: {
      query: Q_ELIGIBLE_AGENCY_CANDIDATES,
      variables() {
        return { userId: this.user.id };
      },
      update(data) {
        // Filter out users already in agency_members
        const memberIds = new Set(this.agency_members.map(m => m.id));
        return (data.artist_users || []).filter(c => !memberIds.has(c.user.id));
      }
    }
  },
  computed: {
    user() {
      return this.$store.getters.getUser;
    }
  },
  methods: {
    async addMember(user) {
      const confirmAdd = await Swal.fire({
        title: "Add agency member?",
        text: `${user.email} will get full access to all agency artists.`,
        type: "warning",
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Yes, add member",
        cancelButtonText: "Cancel",
        confirmButtonClass: "button-default big",
        cancelButtonClass: "button-secondary big",
        buttonsStyling: false
      });

      if (!confirmAdd.value) return;

      try {
        await this.$apollo.mutate({
          mutation: M_ADD_AGENCY_MEMBER,
          client: "localClient",
          variables: { user_id: user.id }
        });
        await this.$apollo.queries.agency_members.refetch();
        await this.$apollo.queries.candidates.refetch();
        await this.$store.dispatch("snack", {
          text: `${user.email} has been added as an agency member.`
        });
      } catch (err) {
        console.log(err);
        await this.$store.dispatch("snack", {
          type: "error",
          text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
        });
      }
    },
    async removeMember(member) {
      const confirmRemove = await Swal.fire({
        title: "Remove agency member?",
        text: `${member.email} will lose access to all agency artists.`,
        type: "warning",
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "Yes, remove access",
        cancelButtonText: "No, keep it",
        confirmButtonClass: "button-danger big",
        cancelButtonClass: "button-secondary big",
        buttonsStyling: false
      });

      if (!confirmRemove.value) return;

      try {
        await this.$apollo.mutate({
          mutation: M_REMOVE_AGENCY_MEMBER,
          client: "localClient",
          variables: { user_id: member.id }
        });
        await this.$apollo.queries.agency_members.refetch();
        await this.$apollo.queries.candidates.refetch();
        await this.$store.dispatch("snack", {
          text: "Agency member removed successfully."
        });
      } catch (err) {
        console.log(err);
        await this.$store.dispatch("snack", {
          type: "error",
          text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.artist-team-card {
  padding: 24px;
  background: $bg-type-2;
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;

  .card-content {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;

    table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0 6px;
      margin-top: 6px;

      thead tr th {
        padding: 18px 18px 6px 0;
        font-weight: 600;
        font-size: 12px;
        line-height: 15px;
        color: $text-type-0;

        &:first-child {
          padding-left: 18px;
        }

        .th-content {
          display: flex;
          align-items: center;
          &.actions {
            padding-left: 6px;
          }
        }
      }

      tbody tr {
        background: $bg-type-1;

        .email {
          border-radius: 10px 0 0 10px;
          padding-left: 18px;

          span {
            font-size: 14px;
            line-height: 28px;
            color: white;
            mix-blend-mode: normal;
            opacity: 0.9;
          }
        }

        .actions {
          border-radius: 0 10px 10px 0;

          .actions-container {
            display: flex;

            button {
              display: flex;
              align-items: center;
              background: none;
              border: none;

              &:focus {
                outline: none;
              }

              span {
                color: $purple;
                font-weight: 600;
                font-size: 14px;
                line-height: 22px;
              }

              svg {
                width: 18px;
                margin-left: 8px;
                path {
                  fill: $purple;
                }
              }
            }

            .button-default span {
              color: white;
            }
          }
        }

        td {
          margin-bottom: 6px;
          font-size: 14px;
          line-height: 28px;
          color: white;
          mix-blend-mode: normal;
          opacity: 0.9;
        }
      }
    }

    .no-members-text {
      width: 100%;
      font-size: 14px;
      line-height: 17px;
      color: $text-type-0;
      opacity: 0.9;
      margin: 12px 0;
    }

    .invite-user-box {
      margin-top: 24px;
      width: 100%;
      border: 2px solid $bg-type-1;
      box-sizing: border-box;
      border-radius: 10px;
      display: flex;
      flex-wrap: wrap;

      .box-header {
        width: 100%;
        padding: 18px 24px;
        display: flex;
        align-items: center;
        background: $bg-type-1;
        border-radius: 8px;

        svg {
          width: 36px;
          path {
            fill: white;
          }
        }

        span {
          margin-left: 20px;
          font-weight: 600;
          font-size: 16px;
          line-height: 24px;
          color: white;
          mix-blend-mode: normal;
          opacity: 0.9;
        }
      }

      .box-content {
        padding: 24px;
        width: 100%;
        display: flex;
        justify-content: center;
        flex-wrap: wrap;

        .box-text {
          width: 100%;
          font-size: 14px;
          line-height: 17px;
          color: $text-type-0;
          mix-blend-mode: normal;
          opacity: 0.9;
        }
      }
    }
  }
}

.table-container.full-width {
  width: 100%;
}

@media (max-width: 600px) {
  .table-container {
    overflow: auto;
  }
}
</style>
